import { LocalStorageCachedKeys } from 'constants/localStorageStoreKeys';
import { Resources } from 'constants/resources';

// eslint-disable-next-line import/order
import { useContext, useEffect } from 'react';

import './styles/global.scss';
import './styles/reactJsonViewCompare.scss';

import { HydraAdmin, ResourceGuesser, useIntrospection } from '@api-platform/admin';
import { API_PATH } from 'config/entrypoint';
import { localStorageStore } from 'config/localStorageStore';
import { APIContext } from 'context/apiContext';
import { usePermissions } from 'hooks/usePermissions';
import { Layout } from 'layouts/Layout';
import { apiClientsResource } from 'pages/ApiClients';
import { Login } from 'pages/Auth/Login/Login';
import { RestorePassword } from 'pages/Auth/RestorePassword/RestorePassword';
import { SetNewPassword } from 'pages/Auth/SetNewPassword/SetNewPassword';
import { commissionsResource } from 'pages/Commissions';
import { CommissionsCreateNewVersion } from 'pages/Commissions/CommissionsCreateNewVersion/CommissionsCreateNewVersion';
import { companiesResource } from 'pages/Companies';
import { DashboardResource } from 'pages/Dashboard';
import { balanceReportResource } from 'pages/Finance/BalanceReport';
import { bankOperationsResource } from 'pages/Finance/BankOperations';
import { commissionRecalculationTaskResource } from 'pages/Finance/CommissionRecalculationTask';
import { cumulativeReportResource } from 'pages/Finance/CumulativeReport';
import { expenseReportResource } from 'pages/Finance/ExpenseReport';
import { ordersReportResource } from 'pages/Finance/OrdersReport';
import { providerAccountsResource } from 'pages/Finance/ProviderAccounts';
import { reconciliationResource } from 'pages/Finance/Reconciliation';
import { refundChargebackReportResource } from 'pages/Finance/RefundChargebackReport';
import { requestsBankOpsResource } from 'pages/Finance/RequestsBankOps';
import { workTeamsResource } from 'pages/Finance/WorkTeams';
import { gatewayRulesResource } from 'pages/GatewayRules';
import { gatewaysResource } from 'pages/Gateways';
import { merchantsResource } from 'pages/Merchants';
import { methodLogosResource } from 'pages/MethodLogos';
import { methodsResource } from 'pages/Methods';
import { monitoringSettingsResource } from 'pages/Monitoring/MonitoringSettings';
import { orderConversionResource } from 'pages/Monitoring/OrderConversion';
import { orderConversionChartResource } from 'pages/Monitoring/OrderConversionChart';
import { orderProviderExternalErrorsResource } from 'pages/Monitoring/OrderProviderExternalErrors';
import { NotFound } from 'pages/NotFound';
import { orderAdjustmentsResource } from 'pages/OrderAdjustments';
import { orderAdjustmentsReportResource } from 'pages/OrderAdjustmentsReport';
import { orderMethodsResource } from 'pages/OrderMethods';
import { ordersResource } from 'pages/Orders';
import { presetsResource } from 'pages/Presets';
import { providersResource } from 'pages/Providers';
import { referralsResource } from 'pages/Referrals';
import { rolesResource } from 'pages/Roles';
import { userActionsResource } from 'pages/UserActions';
import { usersResource } from 'pages/Users';
import { authProvider } from 'providers/authProvider';
import { dataProvider } from 'providers/dataProvider';
import { i18nProvider } from 'providers/i18n/i18nProvider';
import { queryClient } from 'providers/queryClient';
import { CustomRoutes, Resource } from 'react-admin';
import { createBrowserRouter, Route, RouterProvider } from 'react-router-dom';
import { Loading } from 'shared/react-admin/Loading/Loading/Loading';
import { Notification } from 'shared/react-admin/Notification/Notification';
import { ordersBulkUpdateFormResource } from 'shared/widgets/OrdersBulkUpdateForm';
import { baseTheme } from 'themes/baseTheme';
import { Permission } from 'types/embedded/permission';

import { OrderManualCreate } from './pages/OrderManualCreate/OrderManualCreate';

// eslint-disable-next-line import/order
import { Role } from 'constants/roles';

const App = () => {
  const introspect = useIntrospection();
  const { user } = useContext(APIContext);

  useEffect(() => {
    introspect();
  }, []);

  const { getResourcePropsByPermissions, hasResourcePermissions, isHaveRole } = usePermissions();
  const router = createBrowserRouter([
    {
      path: '*',
      element: (
        <HydraAdmin
          authProvider={authProvider}
          catchAll={NotFound}
          dataProvider={dataProvider}
          defaultTheme="light"
          disableTelemetry
          entrypoint={API_PATH}
          i18nProvider={i18nProvider}
          layout={Layout}
          loading={Loading}
          loginPage={Login}
          notification={Notification}
          queryClient={queryClient}
          store={localStorageStore({
            version: '2',
            appKey: '',
            disabledCacheKeys: [LocalStorageCachedKeys.ListParams],
          })}
          theme={baseTheme}
        >
          {!user?.isLoaded ? (
            <CustomRoutes noLayout>
              <Route element={<RestorePassword />} path="restore_password" />
              <Route element={<SetNewPassword />} path="new_password/:token" />
            </CustomRoutes>
          ) : (
            <CustomRoutes>
              <Route
                element={
                  hasResourcePermissions(Resources.Commissions, [
                    Permission.Create,
                    Permission.Full,
                  ]) ? (
                    <CommissionsCreateNewVersion />
                  ) : (
                    <NotFound />
                  )
                }
                path="commissions/create-new"
              />
            </CustomRoutes>
          )}
          <CustomRoutes>
            <Route
              element={
                !user?.isLoaded ? (
                  <Loading />
                ) : isHaveRole([Role.Admin]) ? (
                  <OrderManualCreate />
                ) : (
                  <NotFound />
                )
              }
              path="order_manual_create"
            />
          </CustomRoutes>
          <Resource {...getResourcePropsByPermissions(balanceReportResource)} />
          <Resource {...getResourcePropsByPermissions(expenseReportResource)} />
          <Resource {...getResourcePropsByPermissions(bankOperationsResource)} />
          <Resource {...getResourcePropsByPermissions(methodLogosResource)} />
          <Resource {...getResourcePropsByPermissions(commissionsResource)} />
          <Resource {...getResourcePropsByPermissions(orderAdjustmentsReportResource)} />
          <Resource {...getResourcePropsByPermissions(ordersBulkUpdateFormResource)} />
          <Resource {...getResourcePropsByPermissions(cumulativeReportResource)} />
          <Resource {...getResourcePropsByPermissions(refundChargebackReportResource)} />
          <Resource {...getResourcePropsByPermissions(orderProviderExternalErrorsResource)} />
          <Resource {...getResourcePropsByPermissions(orderConversionChartResource)} />
          <Resource {...getResourcePropsByPermissions(ordersReportResource)} />
          <Resource {...getResourcePropsByPermissions(gatewaysResource)} />
          <Resource name={Resources.Limits} />
          <Resource {...getResourcePropsByPermissions(presetsResource)} />
          <Resource {...getResourcePropsByPermissions(orderConversionResource)} />
          <Resource {...getResourcePropsByPermissions(monitoringSettingsResource)} />
          <Resource {...getResourcePropsByPermissions(orderMethodsResource)} />
          <Resource {...getResourcePropsByPermissions(DashboardResource)} />
          <Resource {...getResourcePropsByPermissions(reconciliationResource)} />
          <Resource {...getResourcePropsByPermissions(referralsResource)} />
          <Resource name={Resources.ExpenseTypes} />
          <Resource name={Resources.ExpenseArticles} />
          <Resource name={Resources.ExpenseSubarticles} />
          <Resource name={Resources.ImportFileStatuses} />
          <Resource name={Resources.NewOrderStatuses} />
          <Resource name={Resources.BankOperationRequestViews} />
          <Resource name={Resources.Currencies} />
          <Resource name={Resources.OrderStatuses} />
          <Resource name={Resources.Transactions} />
          <Resource name={Resources.MerchantNotificationSettings} />
          <Resource name={Resources.TransactionStatuses} />
          <Resource name={Resources.TransactionTypes} />
          <Resource name={Resources.MerchantReferences} />
          <Resource name={Resources.CommissionRecalculationStatuses} />
          <Resource name={Resources.OrderActionUrls} />
          <Resource name={Resources.OrderNotificationUrls} />
          <Resource name={Resources.ApiBankOperationsFinalStatusesGetCollection} />
          <Resource name={Resources.OrderCustomDatas} />
          <Resource name={Resources.OrderDisputes} />
          <Resource name={Resources.OrderNotifications} />
          <Resource name={Resources.OrderEvents} />
          <Resource name={Resources.ConditionGroups} />
          <Resource name={Resources.Expressions} />
          <Resource name={Resources.ExpressionParams} />
          <Resource name={Resources.ComparisonOperators} />
          <Resource name={Resources.Countries} />
          <Resource name={Resources.AdjustmentStatuses} />
          <Resource name={Resources.AdjustmentTypes} />
          <Resource name={Resources.CommissionStatuses} />
          <Resource name={Resources.CommissionComments} />
          <Resource
            {...getResourcePropsByPermissions({
              name: Resources.MethodTypes,
            })}
          />
          <ResourceGuesser {...getResourcePropsByPermissions(userActionsResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(apiClientsResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(merchantsResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(companiesResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(methodsResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(workTeamsResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(providersResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(providerAccountsResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(usersResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(requestsBankOpsResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(rolesResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(orderAdjustmentsResource)} />
          <ResourceGuesser {...getResourcePropsByPermissions(ordersResource)} />
          <ResourceGuesser
            {...getResourcePropsByPermissions(commissionRecalculationTaskResource)}
          />
          <ResourceGuesser {...getResourcePropsByPermissions(gatewayRulesResource)} />
        </HydraAdmin>
      ),
    },
  ]);

  return <RouterProvider router={router} />;
};

export default App;
